import { validateFmActionConfirmation } from '../FmActionConfirmation.validation';
import { SPECIES_CODES } from '../charts/palette';
import { hasMapSlot } from '@/shared/config/mapSlotRegistry';
import { hasMediaAsset } from '@/shared/config/mediaRegistry';

const FORBIDDEN_ANYWHERE = new Set([
  'children',
  'className',
  'style',
  'styles',
  'bodyStyle',
  'width',
  'height',
  'minWidth',
  'maxWidth',
  'minHeight',
  'maxHeight',
  'mapComponent',
  'chartComponent',
  'mediaComponent',
  'figureComponent',
]);

const URL_PATTERN = /^(?:https?:|data:|blob:|\/\/)/i;

const CALLBACK_KEYS = new Set([
  'onClick',
  'onChange',
  'onStepChange',
  'onSelectCallout',
  'onLensChange',
  'onRowSelect',
  'onSelect',
  'onGroupChange',
  'onDrillItem',
  'onRefresh',
  'onRetry',
  'onRangeChange',
  'rowTestId',
  'rowAriaLabel',
  'rowActions',
  'onOpen',
]);

// `figure` only occurs on map media, both nested in a story media slot and at the root of a map element.
function isRegisteredMapCallback(key: string, path: string): boolean {
  return (
    (['onFeatureSelect', 'onPlotSelect', 'onStandSelect'].includes(key) &&
      path.endsWith('.figure.slotProps')) ||
    (key === 'onViewportChange' && path.endsWith('.figure.slotProps.mapData')) ||
    (key === 'onSelectionChange' && path.endsWith('.selection'))
  );
}

export function assertNonEmptyString(value: unknown, label: string): asserts value is string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new Error(`Invalid descriptor: ${label} must be non-empty plain text.`);
  }
}

export function assertRecord(
  value: unknown,
  label: string,
): asserts value is Record<string, unknown> {
  if (
    !value ||
    typeof value !== 'object' ||
    Array.isArray(value) ||
    Object.getPrototypeOf(value) !== Object.prototype
  ) {
    throw new Error(`Invalid descriptor: ${label} must be a plain object.`);
  }
}

export function assertFiniteNumber(value: unknown, label: string): asserts value is number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`Invalid descriptor: ${label} must be a finite number.`);
  }
}

export function hasOwn(record: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

export function assertOwn(record: Record<string, unknown>, key: string, label: string): void {
  if (!hasOwn(record, key)) {
    throw new Error(`Invalid descriptor: ${label}.${key} must be an own field.`);
  }
}

export function validateOptionalBoolean(
  record: Record<string, unknown>,
  key: string,
  label: string,
): void {
  if (hasOwn(record, key) && typeof record[key] !== 'boolean') {
    throw new Error(`Invalid descriptor: ${label}.${key} must be a boolean.`);
  }
}

export function validateOptionalFiniteNumber(
  record: Record<string, unknown>,
  key: string,
  label: string,
): void {
  if (hasOwn(record, key) && (typeof record[key] !== 'number' || !Number.isFinite(record[key]))) {
    throw new Error(`Invalid descriptor: ${label}.${key} must be a finite number.`);
  }
}

export function validateOptionalCallback(
  record: Record<string, unknown>,
  key: string,
  label: string,
): void {
  if (hasOwn(record, key) && typeof record[key] !== 'function') {
    throw new Error(`Invalid descriptor: ${label}.${key} must be a function.`);
  }
}

export function validateEnum(
  value: unknown,
  allowed: readonly string[],
  label: string,
  optional = false,
): void {
  if (optional && value === undefined) return;
  if (typeof value !== 'string' || !allowed.includes(value)) {
    throw new Error(`Invalid descriptor: ${label} must be one of ${allowed.join(', ')}.`);
  }
}

export function assertArray(value: unknown, label: string): asserts value is unknown[] {
  if (!Array.isArray(value)) {
    throw new Error(`Invalid descriptor: ${label} must be an array.`);
  }
}

export function rejectUnknownKeys(
  record: Record<string, unknown>,
  allowed: readonly string[],
  label: string,
): void {
  const allowedSet = new Set(allowed);
  const unknown = Object.keys(record).find((key) => !allowedSet.has(key));
  if (unknown) throw new Error(`Invalid descriptor: unknown ${label} key "${unknown}".`);
}

export function validateOptionalString(
  record: Record<string, unknown>,
  key: string,
  label: string,
): void {
  if (Object.prototype.hasOwnProperty.call(record, key)) {
    assertNonEmptyString(record[key], `${label}.${key}`);
  }
}

export function validateAction(value: unknown, label: string): void {
  assertRecord(value, label);
  rejectUnknownKeys(
    value,
    ['label', 'href', 'onClick', 'testId', 'disabled', 'disabledReason', 'confirmation'],
    label,
  );
  assertNonEmptyString(value.label, `${label}.label`);
  validateOptionalString(value, 'testId', label);
  const hasHref = Object.prototype.hasOwnProperty.call(value, 'href');
  const hasClick = Object.prototype.hasOwnProperty.call(value, 'onClick');
  const disabled = value.disabled === true;
  if (hasHref) assertNonEmptyString(value.href, `${label}.href`);
  if (hasClick && typeof value.onClick !== 'function') {
    throw new Error(`Invalid descriptor: ${label}.onClick must be a function.`);
  }
  if (disabled) {
    if (hasHref || hasClick)
      throw new Error(`Invalid descriptor: disabled ${label} cannot have a target.`);
    assertNonEmptyString(value.disabledReason, `${label}.disabledReason`);
    return;
  }
  if (value.confirmation !== undefined) {
    const confirmationLabel = `${label}.confirmation`;
    validateFmActionConfirmation(value.confirmation, confirmationLabel, (path, message) => {
      throw new Error(`Invalid descriptor: ${path} ${message}.`);
    });
    if (!hasClick) {
      throw new Error(`Invalid descriptor: ${label}.confirmation requires an onClick target.`);
    }
  }
  if (
    Object.prototype.hasOwnProperty.call(value, 'disabled') ||
    Object.prototype.hasOwnProperty.call(value, 'disabledReason')
  ) {
    throw new Error(`Invalid descriptor: ${label} has an invalid disabled contract.`);
  }
  if (!hasHref && !hasClick) throw new Error(`Invalid descriptor: ${label} requires a target.`);
}

export function validateStringArray(value: unknown, label: string): void {
  assertArray(value, label);
  value.forEach((item, index) => assertNonEmptyString(item, `${label}[${index}]`));
}

export function validateContextControl(
  control: unknown,
  itemLabel: string,
): asserts control is Record<string, unknown> {
  assertRecord(control, itemLabel);
  rejectUnknownKeys(
    control,
    ['id', 'label', 'value', 'presentation', 'options', 'onChange', 'disabledReason', 'testId'],
    itemLabel,
  );
  ['id', 'label'].forEach((key) => assertNonEmptyString(control[key], `${itemLabel}.${key}`));
  if (control.value !== null) assertNonEmptyString(control.value, `${itemLabel}.value`);
  validateEnum(control.presentation, ['select', 'segmented', 'radio'], `${itemLabel}.presentation`);
  validateOptionalCallback(control, 'onChange', itemLabel);
  ['disabledReason', 'testId'].forEach((key) => validateOptionalString(control, key, itemLabel));
  assertArray(control.options, `${itemLabel}.options`);
  if (control.options.length === 0) {
    throw new Error(`Invalid descriptor: ${itemLabel}.options must not be empty.`);
  }
  const optionValues = new Set<string>();
  control.options.forEach((option, optionIndex) => {
    const optionLabel = `${itemLabel}.options[${optionIndex}]`;
    assertRecord(option, optionLabel);
    rejectUnknownKeys(option, ['value', 'label', 'disabled'], optionLabel);
    ['value', 'label'].forEach((key) => assertNonEmptyString(option[key], `${optionLabel}.${key}`));
    if (optionValues.has(option.value as string)) {
      throw new Error(
        `Invalid descriptor: duplicate option value "${option.value}" at ${itemLabel}.`,
      );
    }
    optionValues.add(option.value as string);
    validateOptionalBoolean(option, 'disabled', optionLabel);
  });
  if (
    control.value !== null &&
    !control.options.some((option) => {
      assertRecord(option, `${itemLabel}.option`);
      return option.value === control.value;
    })
  ) {
    throw new Error(`Invalid descriptor: ${itemLabel}.value must reference an option.`);
  }
}

const ACCENT_TONES = ['accent', 'teal', 'amber', 'sky', 'rose', 'neutral'] as const;

export function validateTone(record: Record<string, unknown>, label: string): void {
  if (hasOwn(record, 'tone')) validateEnum(record.tone, ACCENT_TONES, `${label}.tone`);
}

export function inspectNested(value: unknown, path: string, root = false): void {
  if (!value || typeof value !== 'object') return;
  if (Array.isArray(value)) {
    value.forEach((item, index) => inspectNested(item, `${path}[${index}]`));
    return;
  }
  const record = value as Record<string, unknown>;
  if ('$\u0024typeof' in record) {
    throw new Error(`Invalid descriptor: JSX/component payload at ${path}.`);
  }
  const sharedEvidenceKind = ['evidence-band', 'evidence-rows', 'source-comparison'].includes(
    String(record.kind),
  );
  if (
    !root &&
    !sharedEvidenceKind &&
    typeof record.kind === 'string' &&
    typeof record.id === 'string' &&
    typeof record.title === 'string'
  ) {
    throw new Error(`Invalid descriptor: nested descriptor container at ${path}.`);
  }
  Object.entries(record).forEach(([key, entry]) => {
    if (FORBIDDEN_ANYWHERE.has(key)) {
      throw new Error(`Invalid descriptor: forbidden field "${key}" at ${path}.`);
    }
    if (key === 'assetKey' && typeof entry === 'string') {
      if (URL_PATTERN.test(entry))
        throw new Error(`Invalid descriptor: raw media URL at ${path}.assetKey.`);
      if (!hasMediaAsset(entry))
        throw new Error(
          `Invalid descriptor: unregistered media asset "${entry}" at ${path}.assetKey.`,
        );
    }
    if (key === 'slotKey' && typeof entry === 'string') {
      if (URL_PATTERN.test(entry))
        throw new Error(`Invalid descriptor: raw map URL at ${path}.slotKey.`);
      if (!hasMapSlot(entry))
        throw new Error(`Invalid descriptor: unregistered map slot "${entry}" at ${path}.slotKey.`);
    }
    if (
      typeof entry === 'function' &&
      !CALLBACK_KEYS.has(key) &&
      !isRegisteredMapCallback(key, path)
    ) {
      throw new Error(`Invalid descriptor: function payload "${key}" at ${path}.`);
    }
    inspectNested(entry, `${path}.${key}`);
  });
}

export function validateChapters(value: unknown, label: string): void {
  assertArray(value, label);
  value.forEach((chapter, index) => {
    const itemLabel = `${label}[${index}]`;
    assertRecord(chapter, itemLabel);
    rejectUnknownKeys(chapter, ['id', 'label', 'meta'], itemLabel);
    assertNonEmptyString(chapter.id, `${itemLabel}.id`);
    assertNonEmptyString(chapter.label, `${itemLabel}.label`);
    validateOptionalString(chapter, 'meta', itemLabel);
  });
}

export function validateRankedRows(value: unknown, label: string): void {
  assertArray(value, label);
  value.forEach((row, index) => {
    const itemLabel = `${label}[${index}]`;
    assertRecord(row, itemLabel);
    rejectUnknownKeys(
      row,
      [
        'key',
        'rank',
        'label',
        'value',
        'score',
        'delta',
        'status',
        'speciesCode',
        'tone',
        'selected',
        'onClick',
        'testId',
      ],
      itemLabel,
    );
    if (row.speciesCode !== undefined && !Number.isInteger(row.speciesCode)) {
      throw new Error(`Invalid descriptor: ${itemLabel}.speciesCode must be an integer species code.`);
    }
    if (row.speciesCode !== undefined && !SPECIES_CODES.includes(Number(row.speciesCode))) {
      throw new Error(
        `Invalid descriptor: ${itemLabel}.speciesCode must be a species code the palette colours; received ${String(row.speciesCode)}.`,
      );
    }
    ['rank', 'label', 'value'].forEach((key) =>
      assertNonEmptyString(row[key], `${itemLabel}.${key}`),
    );
    ['key', 'delta', 'status', 'testId'].forEach((key) =>
      validateOptionalString(row, key, itemLabel),
    );
    // The status line sits under the label and the value at the row end, so the
    // same text in both prints it twice on one row.
    if (typeof row.status === 'string' && row.status.trim() === String(row.value).trim()) {
      throw new Error(
        `Invalid descriptor: ${itemLabel}.status repeats ${itemLabel}.value ("${row.status}"); omit the status.`,
      );
    }
    validateOptionalFiniteNumber(row, 'score', itemLabel);
    validateOptionalBoolean(row, 'selected', itemLabel);
    validateTone(row, itemLabel);
    if (row.onClick !== undefined && typeof row.onClick !== 'function')
      throw new Error(`Invalid descriptor: ${itemLabel}.onClick must be a function.`);
  });
}

export function validateValueExplanations(value: unknown, label: string): void {
  assertArray(value, label);
  value.forEach((explanation, index) => {
    const itemLabel = `${label}[${index}]`;
    assertRecord(explanation, itemLabel);
    rejectUnknownKeys(explanation, ['label', 'value', 'detail', 'tone'], itemLabel);
    assertNonEmptyString(explanation.label, `${itemLabel}.label`);
    assertNonEmptyString(explanation.detail, `${itemLabel}.detail`);
    validateOptionalString(explanation, 'value', itemLabel);
    validateTone(explanation, itemLabel);
  });
}

export function validateBadges(value: unknown, label: string): void {
  assertArray(value, label);
  value.forEach((badge, index) => {
    const itemLabel = `${label}[${index}]`;
    assertRecord(badge, itemLabel);
    rejectUnknownKeys(badge, ['label', 'tone'], itemLabel);
    assertNonEmptyString(badge.label, `${itemLabel}.label`);
    validateTone(badge, itemLabel);
  });
}

export function validateTimelineEvents(value: unknown, label: string): void {
  assertArray(value, label);
  value.forEach((event, index) => {
    const itemLabel = `${label}[${index}]`;
    assertRecord(event, itemLabel);
    rejectUnknownKeys(
      event,
      ['year', 'label', 'detail', 'phase', 'phaseLabel', 'meta', 'content', 'tone', 'testId'],
      itemLabel,
    );
    ['year', 'label', 'detail'].forEach((key) =>
      assertNonEmptyString(event[key], `${itemLabel}.${key}`),
    );
    ['phaseLabel', 'meta', 'content', 'testId'].forEach((key) =>
      validateOptionalString(event, key, itemLabel),
    );
    validateEnum(event.phase, ['past', 'current', 'future'], `${itemLabel}.phase`, true);
    // A phase chip must name the phase the builder declared, not one read off the tone.
    if (event.phaseLabel !== undefined && event.phase === undefined) {
      throw new Error(`Invalid descriptor: ${itemLabel}.phaseLabel requires ${itemLabel}.phase.`);
    }
    validateTone(event, itemLabel);
  });
}

export function validateMetricArray(value: unknown, label: string): void {
  assertArray(value, label);
  value.forEach((metric, index) => {
    const itemLabel = `${label}[${index}]`;
    assertRecord(metric, itemLabel);
    rejectUnknownKeys(
      metric,
      ['label', 'value', 'unit', 'detail', 'status', 'unavailableReason'],
      itemLabel,
    );
    assertNonEmptyString(metric.label, `${itemLabel}.label`);
    if (metric.value === null) {
      assertNonEmptyString(metric.unavailableReason, `${itemLabel}.unavailableReason`);
    } else {
      assertNonEmptyString(metric.value, `${itemLabel}.value`);
      if (metric.unavailableReason !== undefined) {
        throw new Error(
          `Invalid descriptor: ${itemLabel}.unavailableReason is only allowed when value is null.`,
        );
      }
    }
    ['unit', 'detail'].forEach((key) => validateOptionalString(metric, key, itemLabel));
    validateEnum(metric.status, ['good', 'caution', 'loss'], `${itemLabel}.status`, true);
  });
}
